'use client';

import { useState } from 'react';

import { Button, Col, Row, TextField, Typography } from 'darrius';

import { type AxiosRequestConfig } from 'axios';

import { notifyErrorClient } from '../../services/notifyError/client';

import { axiosInstance } from '../../utils/axiosInstance';
import { sha256 } from '../../utils/sha256';

import { Header } from '../../components/atoms';
import { HeadContent, Toast } from '../../components';
import { PIN } from '../../components/molecules';
import { PasswordField } from '../../components/molecules/PasswordField/PasswordField';

import { showToast } from '../../components/atoms/Toast/Toast';

import {
  LoginData,
  LoginDataWithCredentials,
} from '../../data/login/loginStepData';

import { addPhoneMask, onlyNumbers } from '../../utils/parsedMasks';
import { errorPrefix } from '../../utils/errorPrefix';
import { userDataPatterns } from '../../utils/fieldPatterns';
import { navigate } from '../../utils/navigate';

import { initialState, type UserSignupType } from '../../types/UserSignup';

import styles from './LoginTemplate.module.scss';

interface IAuthRequest {
  validate: {
    name: string;
    href: string;
  };
}

const IS_LOGIN_FEATURE_FLAG =
  process.env.NEXT_PUBLIC_FEATURE_FLAG_LOGIN === 'true';

const STEP_BY_STEP = IS_LOGIN_FEATURE_FLAG
  ? LoginDataWithCredentials
  : LoginData;

interface ILoginTemplate {
  redirectTo?: string;
  isCheckoutFlow?: boolean;
  withCredentialsParams?: boolean;
}

export default function LoginTemplate({
  redirectTo,
  isCheckoutFlow = false,
  withCredentialsParams = true,
}: ILoginTemplate) {
  const [stepNumber, setStepNumber] = useState(0);
  const [currentStep, setCurrentStep] = useState(STEP_BY_STEP[0]);
  const [userData, setUserData] = useState<UserSignupType>(initialState);
  const [fieldErrorMessage, setFieldErrorMessage] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const pinInputIsNotCompleted = userData?.pin?.length < 4;
  const pinButtonIsDisabled =
    currentStep.id === 'CODE_VALIDATION' ? pinInputIsNotCompleted : false;
  const phoneInputIsEmpty = userData?.phone?.length === 0;
  const emailIsEmpty = userData?.email?.length === 0;
  const passwordIsEmpty = userData?.password?.length === 0;

  const { id, title, description, components, request } = currentStep;

  function getPayloadRequest(fields) {
    let payload = {};

    fields.forEach((field) => {
      if (field === 'password') {
        const hashPassword = sha256(userData[field]);

        payload = {
          ...payload,
          [field]: hashPassword,
        };

        return;
      }

      if (IS_LOGIN_FEATURE_FLAG && field === 'phone') {
        payload = {
          [field]: onlyNumbers(userData[field]),
          target: 'profile',
          password_flow_app: true,
        };

        return;
      }

      payload = { ...payload, [field]: userData[field] };
    });

    return payload;
  }

  async function executeRequest(
    requestParam,
    withCredentials = true
  ): Promise<IAuthRequest | any> {
    const { path, url: urlParam, fields, method = 'GET' } = requestParam;

    const url = urlParam || path;

    const headers = {
      'Accept-Language': 'pt-BR',
      'Content-Type': 'application/json',
      Accept: '*/*',
    };

    const requestOptions: AxiosRequestConfig = {
      url,
      headers,
      method,
      data: getPayloadRequest(fields),
    };

    const { data } = await axiosInstance(requestOptions, withCredentials);

    return data;
  }

  async function handleNextStep(requestParam) {
    let stepsToAdvance = 1;
    try {
      setIsLoading(true);

      let cookieValue = '';

      if (IS_LOGIN_FEATURE_FLAG) {
        const response = await executeRequest(requestParam, false);

        const { _links = null, token = '', message } = response;

        if (id === 'PHONE') {
          stepsToAdvance = 2;
          setPhoneMessage(message);
        }

        if (
          'login_with_password' in response &&
          response.login_with_password === true
        ) {
          stepsToAdvance = 1;
          setShowPassword(true);
        } else {
          let validate = {
            href: '',
          };

          if (_links) {
            validate = _links.validate;
          }

          cookieValue = token;

          setUserData((oldState) => ({
            ...oldState,
            token: !oldState.token ? token : oldState.token,
            validatePinURL: !oldState.validatePinURL
              ? validate.href
              : oldState.validatePinURL,
          }));
        }
      } else {
        const {
          validate = '',
          token = '',
          message,
        } = await executeRequest(requestParam, withCredentialsParams);

        cookieValue = token;

        if (id === 'PHONE') {
          setPhoneMessage(message);
        }

        setUserData((oldState) => ({
          ...oldState,
          token: !oldState.token ? token : oldState.token,
          validatePinURL: !oldState.validatePinURL
            ? validate.href
            : oldState.validatePinURL,
        }));
      }

      if (id === 'CODE_VALIDATION') {
        const headers = {
          'Accept-Language': 'pt-BR',
          'Content-Type': 'application/json',
          Accept: '*/*',
          Authorization: cookieValue,
        };

        const url = isCheckoutFlow
          ? `${process.env.NEXT_PUBLIC_API_PRO}/auth/profile`
          : '/api/auth/profile?target=painel';

        const requestOptions: AxiosRequestConfig = {
          url,
          method: 'POST',
          headers,
        };

        await axiosInstance(requestOptions, withCredentialsParams);

        const goToRoute = `${redirectTo}${isCheckoutFlow ? `?auth_token=${cookieValue}` : ''}`;

        navigate(goToRoute);

        return;
      }

      setStepNumber((oldState) => oldState + stepsToAdvance);
      setCurrentStep(STEP_BY_STEP[stepNumber + stepsToAdvance]);
      setIsLoading(false);
    } catch (error) {
      if (IS_LOGIN_FEATURE_FLAG) {
        if (STEP_BY_STEP[stepNumber].id === 'CREDENTIALS') {
          setFieldErrorMessage({
            email: 'Credenciais inválidas',
            password: 'Credenciais inválidas',
          });
        }
      }

      if (error?.response?.data?.clientMessage) {
        showToast({
          message: error.response.data.clientMessage,
          type: 'error',
        });
      }

      notifyErrorClient(errorPrefix('[LOGIN-TEMPLATE]', error));
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }

  function handleBackStep() {
    let stepsToRetreat = 1;
    if (IS_LOGIN_FEATURE_FLAG && id === 'CODE_VALIDATION' && !showPassword) {
      stepsToRetreat = 2;
    }
    setStepNumber((oldState) => Math.max(0, oldState - stepsToRetreat));
    setCurrentStep(STEP_BY_STEP[stepNumber - stepsToRetreat]);
    setUserData({ ...userData, pin: '' });
  }

  function handleValidateFields(value, field) {
    if (!value) return 'Campo obrigatório';
    if (
      userDataPatterns[field] &&
      !new RegExp(userDataPatterns[field].regexp).test(value)
    ) {
      return userDataPatterns[field].error;
    }
  }

  function handleChangeValueForm(value, field) {
    const errorMessage = handleValidateFields(value, field);

    if (errorMessage) {
      setFieldErrorMessage({ [field]: errorMessage });
    } else {
      setFieldErrorMessage({});
    }

    setUserData((oldState) => ({
      ...oldState,
      [field]: value,
      phone: field === 'phone' ? addPhoneMask(value) : oldState.phone,
    }));
  }

  function checkIsDisabledButton() {
    const fieldsWithError = Object.keys(fieldErrorMessage).filter(
      (field) => field
    );

    const hasInvalidValue = fieldsWithError.length > 0;

    if (IS_LOGIN_FEATURE_FLAG) {
      if (id === 'PHONE') {
        return hasInvalidValue || isLoading || phoneInputIsEmpty;
      }

      if (id === 'CREDENTIALS_SIGNIN') {
        return hasInvalidValue || isLoading || emailIsEmpty || passwordIsEmpty;
      }

      if (id === 'CODE_VALIDATION' && showPassword) {
        return (
          hasInvalidValue ||
          isLoading ||
          emailIsEmpty ||
          passwordIsEmpty ||
          phoneInputIsEmpty ||
          pinButtonIsDisabled
        );
      }
    }

    return (
      hasInvalidValue || isLoading || phoneInputIsEmpty || pinButtonIsDisabled
    );
  }

  function mountBodyForm(componentsParam, requestParam) {
    return componentsParam.map((component) => {
      if (component.type === 'textfield_phone') {
        const field = component.name;

        return (
          <div key={component.id}>
            <TextField
              id={component.name}
              name={component.name}
              label={component.label}
              placeholder={component.placeholder}
              onChange={(event) => {
                handleChangeValueForm(event.target.value, field);
              }}
              onBlur={(event) => {
                handleChangeValueForm(event.target.value, field);
              }}
              value={userData[field]}
              errorMessage={fieldErrorMessage[component.name]}
            />
          </div>
        );
      }

      if (component.type === 'textfield_email') {
        const field = component.name;

        return (
          <div key={component.id}>
            <TextField
              id={component.name}
              name={component.name}
              label={component.label}
              placeholder={component.placeholder}
              onChange={(event) => {
                handleChangeValueForm(event.target.value, field);
              }}
              onBlur={(event) => {
                handleChangeValueForm(event.target.value, field);
              }}
              value={userData[field]}
              errorMessage={fieldErrorMessage[component.name]}
            />
          </div>
        );
      }

      if (component.type === 'password') {
        const field = component.name;

        return (
          <div key={component.id} className={styles.password__content}>
            <PasswordField
              id={component.name}
              name={component.name}
              label={component.label}
              placeholder={component.placeholder}
              value={userData[field]}
              errorMessage={fieldErrorMessage[component.name]}
              onChange={(event) => {
                handleChangeValueForm(event.target.value, field);
              }}
              onBlur={(event) => {
                handleChangeValueForm(event.target.value, field);
              }}
            />

            <a href="/seja-um-profissional/login/esqueci-minha-senha">
              Esqueci minha senha
            </a>
          </div>
        );
      }

      if (component.type === 'button') {
        return (
          <Button.Root
            key={component.id}
            className="signup__button"
            fullWidth
            disabled={checkIsDisabledButton()}
            onClick={async () => {
              await handleNextStep(requestParam);
            }}
          >
            {!isLoading ? component.label : <>Carregando</>}
          </Button.Root>
        );
      }

      if (component.type === 'button_link') {
        return (
          <Button.Link
            key={component.id}
            href={component.link}
            variation={component.variation}
            className="signup__button"
            fullWidth
          >
            {component.label}
          </Button.Link>
        );
      }

      if (component.type === 'pin') {
        return (
          <div className="signup__pin-code" key={component.id}>
            <img
              src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/phone-validation.svg`}
              alt="Imagem de um telefone celular"
            />
            <Typography.Title className={styles.title} size="3xl">
              Digite o código de 4 dígitos para verificar seu celular
            </Typography.Title>
            <Typography.Text className={styles.text}>
              {phoneMessage || `Enviado para o número ${userData.phone}`}
            </Typography.Text>
            <PIN numberOfDigits={4} handlePinValue={setUserData} />
            <Button.Root
              variation="clear"
              size="small"
              onClick={handleBackStep}
            >
              {IS_LOGIN_FEATURE_FLAG && showPassword
                ? 'Alterar Credenciais'
                : 'Mudar número'}
            </Button.Root>
          </div>
        );
      }
    });
  }

  return (
    <>
      <HeadContent
        slug="Pro login"
        metaTitle="GetNinjas | Orçamento de Profissionais Confiáveis no GetNinjas.com.br"
        metaDescription=""
        noindex
        type="prospect:login"
      />

      <Header.Simple />

      <div className={styles.login}>
        {stepNumber < 2 && IS_LOGIN_FEATURE_FLAG && (
          <div className="center-md center-sm space-bottom- space-top">
            <img
              src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/phone-login.svg`}
              alt=""
            />
          </div>
        )}

        {stepNumber !== 0 && (
          <div className="signup__container__back-button">
            <button type="button" onClick={handleBackStep}>
              <img
                src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/back-arrow.svg`}
                alt="Seta para volta ao passo anterior"
              />
            </button>
          </div>
        )}

        {title && (
          <Row className="center">
            <Col className="col-sm-12 col-md-6">
              <Typography.Title size="3xl" className="center">
                {title}
              </Typography.Title>
              <Typography.Text className={`${styles.text} center`}>
                {description}
              </Typography.Text>
            </Col>
          </Row>
        )}

        <Row className="center-md">
          <Col className={`col-sm-12 col-md-6 start-sm ${styles.login__body}`}>
            {mountBodyForm(components, request)}
          </Col>
        </Row>
      </div>
      <Toast />
    </>
  );
}
